import type { CurrencyFormValue, SettingsForm } from '@/models/cashier-settings/settings-screen.model';
import type {
  CashierConfig,
  CashierConfigDefaultAmountItem,
  CashierConfigSuggestedAmountItem,
} from '@/models/cashier-settings/cashier-settings.model';
import type { CurrencyItem } from '@/models/dictionary/currency-list.model';

export const getMappedPayload = (
  data: Record<string, CurrencyFormValue>,
  country: string,
): CashierConfig => {
  const currencyList = Object.keys(data);

  const defaultAmount = {
    depositList: currencyList.map((currency) => ({
      currency,
      amount: Number(data[currency].data.deposit),
    })),
    withdrawalList: currencyList.map((currency) => ({
      currency,
      amount: Number(data[currency].data.withdrawal),
    })),
  };

  const subsequentAmountList: CashierConfigDefaultAmountItem[] = currencyList
    .filter((currency: string) => {
      const value = data[currency].data.subsequentAmount;

      return value !== null && value !== undefined;
    })
    .map((currency: string) => ({
      currency,
      amount: Number(data[currency].data.subsequentAmount),
    }));

  const suggestedAmount = {
    depositList: currencyList.map((currency) => {
      const presetData = data[currency].data;
      const presetKeys = Object.keys(presetData)
        .filter((key) => key.startsWith('preset') && presetData[key]);

      return {
        currency,
        presetAmountList: presetKeys.map((presetKey) => Number(presetData[presetKey])),
      };
    }),
    withdrawalList: [],
    defaultList: [50, 100, 200, 500], // требование бэка
  };

  const payload: CashierConfig = {
    country,
    defaultAmount,
    suggestedAmount,
  };

  if (subsequentAmountList.length > 0) {
    payload.subsequentAmount = { depositList: subsequentAmountList };
  }

  return payload;
};

export const getMappedFormDataFromConfig = (
  config: CashierConfig | null,
): Record<CurrencyItem['code'], SettingsForm> | null => {
  if (!config) {
    return null;
  }

  const data: Record<CurrencyItem['code'], Partial<SettingsForm>> = {};

  const ensureCurrencyEntry = (currency: string) => {
    if (!data[currency]) {
      data[currency] = {};
    }

    return data[currency];
  };

  config.defaultAmount?.depositList.forEach((cashierConfig: CashierConfigDefaultAmountItem) => {
    ensureCurrencyEntry(cashierConfig.currency).deposit = cashierConfig.amount;
  });

  config.defaultAmount?.withdrawalList.forEach((cashierConfig: CashierConfigDefaultAmountItem) => {
    ensureCurrencyEntry(cashierConfig.currency).withdrawal = cashierConfig.amount;
  });

  config.subsequentAmount?.depositList.forEach((cashierConfig: CashierConfigDefaultAmountItem) => {
    ensureCurrencyEntry(cashierConfig.currency).subsequentAmount = cashierConfig.amount;
  });

  config.suggestedAmount?.depositList.forEach((cashierConfig: CashierConfigSuggestedAmountItem) => {
    const entry = ensureCurrencyEntry(cashierConfig.currency);

    cashierConfig.presetAmountList.forEach((amount, index) => {
      const key = `preset${index + 1}` as keyof SettingsForm;
      entry[key] = amount;
    });
  });

  return data as Record<CurrencyItem['code'], SettingsForm>;
};
